/**
 * Column definitions for the offers table (ADW tab). Parity:
 * `offers/index.html:1122-1142`. Two fixed columns (Name, Actions)
 * are rendered outside the dynamic column array. Status is dynamic
 * but pinned (cannot be dragged).
 *
 * Column order is persisted per network tab to `localStorage`:
 *   - ADW:   `offersManagerADWColumnOrder`
 *   - Roots: `offersManagerRootsColumnOrder`
 */

export type OfferColumnType = "string" | "number"

export interface OfferColumn {
  id: string
  label: string
  type: OfferColumnType
  /** Metric key in offer.metrics — defaults to `id` when absent. */
  metricKey?: string
  minWidth?: string
}

export const ADW_COLUMNS: readonly OfferColumn[] = [
  { id: "status", label: "Status", type: "string" },
  { id: "visits", label: "Visits", type: "number" },
  { id: "second_flow_visits", label: "2Flow", type: "number" },
  { id: "gclicks", label: "GClicks", type: "number" },
  { id: "revenue", label: "Revenue", type: "number" },
  { id: "rpm", label: "RPM", type: "number" },
  { id: "rpc", label: "RPC", type: "number" },
  { id: "flow_1_to_2", label: "1\u21922 Flow", type: "number" },
  { id: "cost", label: "Cost", type: "number" },
  { id: "profit", label: "Profit", type: "number" },
  { id: "roi", label: "ROI", type: "number" },
  { id: "cpa", label: "CPA", type: "number" },
  { id: "conversions", label: "Conversions", type: "number" },
  { id: "clicks", label: "Clicks (TT)", type: "number", metricKey: "clicks" },
  { id: "cpc", label: "CPC", type: "number" },
  { id: "cpm", label: "CPM", type: "number" },
  { id: "ctr", label: "CTR", type: "number" },
  { id: "cvr", label: "CVR", type: "number" },
  { id: "impressions", label: "Impressions", type: "number", minWidth: "100px" },
] as const

export const PINNED_DYNAMIC_COLUMN = "status"

export const ADW_COLUMN_STORAGE_KEY = "offersManagerADWColumnOrder"
export const ROOTS_COLUMN_STORAGE_KEY = "offersManagerRootsColumnOrder"
export const ADW_VISIBILITY_STORAGE_KEY = "offersManagerADWColumnVisibility"
export const ROOTS_VISIBILITY_STORAGE_KEY = "offersManagerRootsColumnVisibility"
export const KLOW_VISIBILITY_STORAGE_KEY = "offersManagerKlowColumnVisibility"

/**
 * Load the saved column order from localStorage. Discards stale
 * saves where the id set doesn't match the current defaults.
 * Parity: `offers/index.html:1154-1178`.
 */
export function loadColumnOrder(
  storageKey: string,
  defaults: readonly OfferColumn[],
): string[] {
  try {
    const saved = globalThis.localStorage?.getItem(storageKey)
    if (saved) {
      const parsed: unknown = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        const ids = parsed.map((entry) =>
          typeof entry === "string"
            ? entry
            : entry && typeof entry === "object" && "id" in entry
              ? String((entry as { id: unknown }).id)
              : "",
        )
        const savedSet = new Set(ids.filter(Boolean))
        const defaultSet = new Set(defaults.map((c) => c.id))
        if (
          savedSet.size === defaultSet.size &&
          [...savedSet].every((id) => defaultSet.has(id))
        ) {
          // Ensure status stays first
          const result = ids.filter(Boolean)
          const statusIdx = result.indexOf(PINNED_DYNAMIC_COLUMN)
          if (statusIdx > 0) {
            result.splice(statusIdx, 1)
            result.unshift(PINNED_DYNAMIC_COLUMN)
          }
          return result
        }
      }
    }
  } catch {
    // Fall through to defaults.
  }
  return defaults.map((c) => c.id)
}

export function saveColumnOrder(storageKey: string, order: string[]): void {
  try {
    globalThis.localStorage?.setItem(storageKey, JSON.stringify(order))
  } catch {
    // Silently ignore.
  }
}

/** Load column visibility map. Returns a Set of hidden column IDs. */
export function loadColumnVisibility(storageKey: string): Set<string> {
  try {
    const saved = globalThis.localStorage?.getItem(storageKey)
    if (saved) {
      const parsed: unknown = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((x): x is string => typeof x === "string"))
      }
    }
  } catch {
    // Fall through to defaults.
  }
  return new Set()
}

export function saveColumnVisibility(
  storageKey: string,
  hiddenIds: Set<string>,
): void {
  try {
    globalThis.localStorage?.setItem(
      storageKey,
      JSON.stringify([...hiddenIds]),
    )
  } catch {
    // Silently ignore.
  }
}
